import React from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';

export const BasicInfoCard = ({ formData, setFormData }) => (
  <Card className="p-8">
    <h2 className="text-2xl font-serif font-normal text-[#14213D] mb-6">Basic Information</h2>
    <div className="space-y-4">
      <div>
        <Label htmlFor="title">Unit Title *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          data-testid="edit-unit-title-input"
          required
        />
      </div>
      <div>
        <Label htmlFor="description">Description *</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={4}
          data-testid="edit-unit-description-input"
          required
        />
      </div>
      <div>
        <Label htmlFor="teacher_notes">Teacher Notes</Label>
        <Textarea
          id="teacher_notes"
          value={formData.teacher_notes}
          onChange={(e) => setFormData({ ...formData, teacher_notes: e.target.value })}
          rows={3}
          data-testid="edit-teacher-notes-input"
        />
      </div>
    </div>
  </Card>
);

export const StandardsCard = ({ standards, ops }) => (
  <Card className="p-8">
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-2xl font-serif font-normal text-[#14213D]">Kansas HGSS Standards</h2>
      <Button type="button" variant="outline" size="sm" onClick={() => ops.add({ code: '', description: '' })}>
        <Plus className="h-4 w-4 mr-2" />
        Add Standard
      </Button>
    </div>
    <div className="space-y-4">
      {standards.map((standard, index) => (
        <div key={standard._uid} className="flex gap-4 items-start border border-[#EBE7E0] rounded-lg p-4">
          <div className="flex-1 space-y-3">
            <Input
              placeholder="Standard Code"
              value={standard.code}
              onChange={(e) => ops.update(index, 'code', e.target.value)}
            />
            <Textarea
              placeholder="Standard Description"
              value={standard.description}
              onChange={(e) => ops.update(index, 'description', e.target.value)}
              rows={2}
            />
          </div>
          {standards.length > 1 && (
            <Button type="button" variant="ghost" size="sm" onClick={() => ops.remove(index)}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}
    </div>
  </Card>
);

export const VocabularyCard = ({ vocabulary, ops }) => (
  <Card className="p-8">
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-2xl font-serif font-normal text-[#14213D]">Vocabulary</h2>
      <Button type="button" variant="outline" size="sm" onClick={() => ops.add({ term: '', definition: '', context: '' })} data-testid="add-vocabulary-btn">
        <Plus className="h-4 w-4 mr-2" />
        Add Term
      </Button>
    </div>
    <div className="space-y-4">
      {vocabulary.map((item, index) => (
        <div key={item._uid} className="flex gap-4 items-start border border-[#EBE7E0] rounded-lg p-4">
          <div className="flex-1 space-y-3">
            <Input
              placeholder="Term"
              value={item.term}
              onChange={(e) => ops.update(index, 'term', e.target.value)}
              data-testid={`vocab-term-${index}`}
            />
            <Textarea
              placeholder="Definition"
              value={item.definition}
              onChange={(e) => ops.update(index, 'definition', e.target.value)}
              rows={2}
              data-testid={`vocab-definition-${index}`}
            />
            <Input
              placeholder="Context (optional)"
              value={item.context}
              onChange={(e) => ops.update(index, 'context', e.target.value)}
            />
          </div>
          {vocabulary.length > 1 && (
            <Button type="button" variant="ghost" size="sm" onClick={() => ops.remove(index)}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}
    </div>
  </Card>
);

export const IdentificationsCard = ({ identifications, ops, connectionOps, blankIdentification }) => (
  <Card className="p-8">
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-2xl font-serif font-normal text-[#14213D]">Key Identifications</h2>
      <Button type="button" variant="outline" size="sm" onClick={() => ops.add(blankIdentification())} data-testid="add-identification-btn">
        <Plus className="h-4 w-4 mr-2" />
        Add Identification
      </Button>
    </div>
    <div className="space-y-6">
      {identifications.map((item, index) => (
        <div key={item._uid} className="border border-[#EBE7E0] rounded-lg p-4 space-y-4">
          <div className="flex justify-between items-start">
            <select
              value={item.type}
              onChange={(e) => ops.update(index, 'type', e.target.value)}
              className="border border-[#EBE7E0] rounded px-3 py-1 text-sm"
              data-testid={`identification-type-${index}`}
            >
              <option value="person">Person</option>
              <option value="event">Event</option>
              <option value="theme">Theme</option>
            </select>
            {identifications.length > 1 && (
              <Button type="button" variant="ghost" size="sm" onClick={() => ops.remove(index)}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <Input
            placeholder="Name"
            value={item.name}
            onChange={(e) => ops.update(index, 'name', e.target.value)}
            data-testid={`identification-name-${index}`}
          />
          <Textarea
            placeholder="Description"
            value={item.description}
            onChange={(e) => ops.update(index, 'description', e.target.value)}
            rows={2}
            data-testid={`identification-description-${index}`}
          />
          <Textarea
            placeholder="Significance"
            value={item.significance}
            onChange={(e) => ops.update(index, 'significance', e.target.value)}
            rows={2}
            data-testid={`identification-significance-${index}`}
          />
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">Connections</Label>
              <Button type="button" variant="ghost" size="sm" onClick={() => connectionOps.add(index)}>
                <Plus className="h-3 w-3 mr-1" />
                Add Connection
              </Button>
            </div>
            {item.connections.map((conn, connIndex) => (
              <div key={conn._uid} className="flex gap-2 mb-2">
                <Input
                  placeholder="Connection"
                  value={conn.value}
                  onChange={(e) => connectionOps.update(index, connIndex, e.target.value)}
                />
                {item.connections.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => connectionOps.remove(index, connIndex)}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </Card>
);
